import axios from 'axios'

const apiUrl = import.meta.env.VITE_API_URL

export type EmbeddingRolloutState =
  | 'backfilling'
  | 'verifying'
  | 'ready'
  | 'enabled'
  | 'disabled'
  | 'failed'

export interface OrgEmbeddingRollout {
  org_uuid: string
  target_profile: string
  state: EmbeddingRolloutState
  docs_run_id: string
  memories_run_id: string
  model: string
  model_revision: string
  vector_size: number
  docs_source_count: number | null
  docs_target_count: number | null
  memories_source_count: number | null
  memories_target_count: number | null
  verified_at: string | null
  enabled_at: string | null
  disabled_at: string | null
  post_enable_write_at: string | null
  version: number
  last_error: string | null
  updated_by: string
  created_at: string
  updated_at: string
  local_embeddings_enabled: boolean
}

export interface StartEmbeddingRolloutRequest {
  docs_run_id: string
  memories_run_id: string
}

const path = (orgUuid: string, action = '') =>
  `${apiUrl}/orgs/${encodeURIComponent(orgUuid)}/embedding-rollout${action}`

export async function getOrgEmbeddingRollout(
  orgUuid: string,
): Promise<OrgEmbeddingRollout | null> {
  const response = await axios.get<OrgEmbeddingRollout | null>(path(orgUuid))
  return response.data
}

export async function startOrgEmbeddingRollout(
  orgUuid: string,
  request: StartEmbeddingRolloutRequest,
): Promise<OrgEmbeddingRollout> {
  const response = await axios.post<OrgEmbeddingRollout>(
    path(orgUuid, '/start'),
    request,
  )
  return response.data
}

export async function verifyOrgEmbeddingRollout(
  orgUuid: string,
): Promise<OrgEmbeddingRollout> {
  const response = await axios.post<OrgEmbeddingRollout>(
    path(orgUuid, '/verify'),
  )
  return response.data
}

export async function activateOrgEmbeddingRollout(
  orgUuid: string,
): Promise<OrgEmbeddingRollout> {
  const response = await axios.post<OrgEmbeddingRollout>(
    path(orgUuid, '/activate'),
  )
  return response.data
}

export async function disableOrgEmbeddingRollout(
  orgUuid: string,
): Promise<OrgEmbeddingRollout> {
  const response = await axios.post<OrgEmbeddingRollout>(
    path(orgUuid, '/disable'),
  )
  return response.data
}

export function embeddingRolloutError(
  error: unknown,
  fallback: string,
): { message: string; status?: number } {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: unknown } }
    }
  )?.response
  return {
    message:
      typeof response?.data?.detail === 'string'
        ? response.data.detail
        : fallback,
    status: response?.status,
  }
}
